import CopyableText from 'components/atoms/buttons/copyable-text';
import i18n from 'infrastructure/i18n';

import s from './styles.module.scss';
import TimezoneCol from './timezone';
import LocalTimeCol from './local-time';
import CampaignActionsCol from './campaign-actions';
import ContactAvailabilityCol from './contact-availability';
import CampaignStatusCol from './campaign-status';
import OutcomeCol from './outcome';
import Name from './name';
import LastSent from './last-sent';
import NumberColumn from './number';

import type { TableProps } from 'antd';
import type { ISmsCampaign } from 'infrastructure/interfaces';

interface IProps {
  page: number;
  pageSize: number;
  sending: boolean;
  uploadCampaign?: ISmsCampaign;
  onSend: (rec: ISmsCampaign[]) => void;
  onOpen: (rec: ISmsCampaign) => void;
}

export const useColumns = ({
  page,
  pageSize,
  sending,
  onSend,
  onOpen,
  uploadCampaign,
}: IProps): TableProps<ISmsCampaign>['columns'] => [
  {
    title: '№',
    key: 'guid',
    width: 50,
    align: 'center',
    render: (_, __, idx) => (
      <NumberColumn page={page} pageSize={pageSize} idx={idx} />
    ),
  },
  {
    title: i18n.t('labels.patientName'),
    key: 'firstName',
    sorter: true,
    render: (_, rec) => <Name data={rec} />,
  },
  {
    title: i18n.t('labels.ptID'),
    key: 'guid',
    width: 90,
    align: 'center',
    render: (_, rec) => (
      <CopyableText
        label={rec.patient.guid}
        value={rec.patient.guid}
        className={s.number}
      >
        {i18n.t('controls.copy')}
      </CopyableText>
    ),
  },
  {
    title: i18n.t('labels.primaryPhoneNumber'),
    key: 'primaryPhoneNumber',
    width: 160,
    render: (_, rec) => <span className={s.text}>{rec.patient.phone}</span>,
  },
  {
    title: i18n.t('labels.timezone'),
    key: 'timezone',
    width: 120,
    align: 'center',
    render: (_, rec) => <TimezoneCol timezone={rec.patient.timezone} />,
  },
  {
    title: i18n.t('labels.localTime'),
    key: 'localTime',
    width: 120,
    align: 'center',
    render: (_, rec) => <LocalTimeCol timezone={rec.patient.timezone} />,
  },
  {
    title: i18n.t('labels.contactAvailability'),
    key: 'contactAvailability',
    width: 140,
    align: 'center',
    render: (_, rec) => <ContactAvailabilityCol data={rec} />,
  },
  {
    title: i18n.t('labels.sendCount'),
    key: 'sendCount',
    width: 100,
    align: 'center',
    render: (_, rec) => <span className={s.text}>{rec.sendCount}</span>,
  },
  {
    title: i18n.t('labels.lastSentAt'),
    key: 'lastSentAt',
    width: 180,
    align: 'center',
    render: (_, rec) => <LastSent lastSentAt={rec?.lastSentAt} />,
  },
  {
    title: i18n.t('labels.campaignActions'),
    key: 'campaignActions',
    width: 140,
    render: (_, rec) => (
      <CampaignActionsCol
        data={rec}
        loading={uploadCampaign?.guid === rec.guid && sending}
        onSend={() => onSend([rec])}
      />
    ),
  },
  {
    title: i18n.t('labels.campaignStatus'),
    key: 'campaignStatus',
    width: 135,
    align: 'center',
    render: (_, rec) => (
      <CampaignStatusCol status={rec?.status} errorCode={rec.errorCode} />
    ),
  },
  {
    title: i18n.t('labels.outcome'),
    key: 'outcome',
    width: 120,
    align: 'center',
    render: (_, rec) => (
      <OutcomeCol status={rec?.status} onOpen={() => onOpen(rec)} />
    ),
  },
];
